/**
 * ShapeRenderer.tsx
 * Composant pour rendre une forme selon son type
 * Extrait de CanvasArea.tsx pour améliorer la maintenabilité
 */
import React, { useCallback } from 'react';
import { Rect, Circle, Text, Line, Star, Arrow, Path, Group, Image } from 'react-konva';
import Konva from 'konva';
import type { ShapeData, BlendMode } from '../../../types/ShapeTypes';
import { useAppStore } from '../../../services/StateService';
import { AlignmentGuides } from '../../../AlignmentGuides';
import { logger } from '../../../utils/ProfessionalLogger';
// FIX runtime : `require()` n'existe pas en ESM/Vite (navigateur) → "require is
// not defined" à chaque transform/drag. On importe statiquement à la place.
import { applyShapeTransform, commitShapeTransform } from '../../../utils/transformUtils';

/**
 * Convertit notre `BlendMode` (qui exposait `'normal'`) vers la valeur Konva
 * `globalCompositeOperationType`, qui exige `'source-over'` à la place.
 */
function toGlobalCompositeOperation(mode: BlendMode | undefined): GlobalCompositeOperation {
  if (!mode || mode === 'normal') return 'source-over';
  return mode as GlobalCompositeOperation;
}

/**
 * PR-10: Contexte pour le group drag
 */
type GroupDragContext = {
  ids: string[];
  primaryId: string;
  primaryStart: { x: number; y: number };
  startPositions: Record<string, { x: number; y: number }>;
};

/**
 * Composant pour rendre une image avec chargement asynchrone
 */
const ImageRenderer: React.FC<{
  shape: ShapeData;
  commonProps: Record<string, unknown>;
}> = ({ shape, commonProps }) => {
  const [imageElement, setImageElement] = React.useState<HTMLImageElement | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (shape.src) {
      setIsLoading(true);

      // Résoudre les URLs snapbooth-asset:// depuis AssetsLibrary
      // P0.6 FIX: Résoudre les storage paths formatés (supabase://) en signed URLs
      const resolveAssetUrl = async (src: string): Promise<string> => {
        // Résoudre snapbooth-asset://
        if (src.startsWith('snapbooth-asset://')) {
          const assetId = src.replace('snapbooth-asset://', '');
          const { AssetsLibrary } = await import('../../../AssetsLibrary');
          const assetsLibrary = AssetsLibrary.getInstance();
          const asset = assetsLibrary.getAsset(assetId);
          if (asset && asset.dataURL) {
            return asset.dataURL;
          }
          logger.warn(`Asset non trouvé: ${assetId}`);
          return src;
        }

        // P0.6 FIX: Résoudre supabase://storage-path en signed URL
        if (src.startsWith('supabase://')) {
          try {
            const { ensureSignedUrl } = await import('../../../services/StorageUrlService');
            return await ensureSignedUrl(src);
          } catch (error) {
            logger.error(`Erreur lors de la résolution du storage path: ${src}`, error);
            return src;
          }
        }

        return src;
      };

      resolveAssetUrl(shape.src).then(resolvedSrc => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setImageElement(img);
          setIsLoading(false);
        };
        img.onerror = () => {
          logger.error("Erreur lors du chargement de l'image:", { resolvedSrc });
          setIsLoading(false);
        };
        img.src = resolvedSrc;
      });
    } else {
      setIsLoading(false);
    }
  }, [shape.src]);

  if (isLoading && shape.src) {
    return (
      <Rect
        {...commonProps}
        width={shape.width || 200}
        height={shape.height || 150}
        fill="#e5e7eb"
        stroke="#9ca3af"
        strokeWidth={1}
        dash={[5, 5]}
      />
    );
  }

  if (!imageElement || !shape.src) {
    return null;
  }

  return (
    <Image
      {...commonProps}
      image={imageElement}
      width={shape.width || imageElement.width}
      height={shape.height || imageElement.height}
      shadowBlur={shape.shadowBlur || 0}
      shadowColor={shape.shadowColor}
      shadowOffsetX={shape.shadowOffsetX || 0}
      shadowOffsetY={shape.shadowOffsetY || 0}
      globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
    />
  );
};

/**
 * Composant pour rendre une forme selon son type
 */
export const ShapeRenderer: React.FC<{
  shape: ShapeData;
  isSelected: boolean;
  isHovered?: boolean;
  onSelect: () => void;
  onChange: (attrs: Partial<ShapeData>) => void;
  onDoubleClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  alignmentGuides?: AlignmentGuides | null;
}> = ({
  shape,
  isSelected,
  isHovered,
  onSelect,
  onChange,
  onDoubleClick,
  onMouseEnter,
  onMouseLeave,
  alignmentGuides,
}) => {
  // PR-16: handleClick pour gérer Alt+Click (select group)
  const handleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // Empêcher le clic de remonter au Stage (évite la désélection)
    e.cancelBubble = true;
    e.evt.stopPropagation();

    const store = useAppStore.getState();

    // ✅ Alt+click = select group
    if (e.evt.altKey) {
      store.selectGroupOfShape?.(shape.id);
      return;
    }

    // sinon comportement normal existant
    onSelect();
    logger.debug(`🖱️ Clic forme -> Sélection : ${shape.id}`);
  };

  // PR-10: handleDragStart pour gérer le group drag
  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    // PR-17: Sécurité supplémentaire - empêcher drag si locked
    if (shape.locked) return;

    const store = useAppStore.getState();

    // ✅ sélection source de vérité
    const ids = store.selection.selectedIds;
    const isInSelection = ids.includes(shape.id);

    // Drag simple si pas multi-select
    if (!isInSelection || ids.length <= 1) {
      store.beginGesture?.('drag', [shape.id]); // PR-08
      return;
    }

    // ✅ group drag
    const shapes = store.shapes;
    const startPositions: Record<string, { x: number; y: number }> = {};

    for (const id of ids) {
      const s = shapes.find(x => x.id === id);
      if (s) startPositions[id] = { x: s.x ?? 0, y: s.y ?? 0 };
    }

    const node = e.target;
    const ctx: GroupDragContext = {
      ids,
      primaryId: shape.id,
      primaryStart: { x: node.x(), y: node.y() },
      startPositions,
    };

    node.setAttr('__groupDrag', ctx);

    store.beginGesture?.('drag', ids); // 1 seule gesture pour tout le groupe
  };

  // PR-10: handleDragMove (déplacer les autres nodes en live)
  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    const node = e.target;

    // ✅ Snap/guides sur le node primary
    if (alignmentGuides && alignmentGuides.isEnabled() && alignmentGuides.isShowGuidesEnabled()) {
      const alignedPos = alignmentGuides.checkAlignment(node);
      if (alignmentGuides.isSnapToGuidesEnabled()) {
        node.position({ x: alignedPos.x, y: alignedPos.y });
      }
    }

    const ctx = node.getAttr('__groupDrag') as GroupDragContext | undefined;
    if (!ctx) return;

    const stage = node.getStage();
    if (!stage) return;

    const dx = node.x() - ctx.primaryStart.x;
    const dy = node.y() - ctx.primaryStart.y;

    // ✅ déplacer les autres nodes sélectionnés
    for (const id of ctx.ids) {
      if (id === ctx.primaryId) continue;

      const other = stage.findOne(`#${id}`) as Konva.Node | null;
      if (!other) continue;

      const start = ctx.startPositions[id];
      if (!start) continue;

      other.position({ x: start.x + dx, y: start.y + dy });
    }

    stage.batchDraw();
  };

  // PR-10: handleDragEnd (commit store 1 seule fois)
  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    // PR-17: Sécurité supplémentaire
    if (shape.locked) return;

    const store = useAppStore.getState();
    const node = e.target;

    alignmentGuides?.clearGuides?.();

    const ctx = node.getAttr('__groupDrag') as GroupDragContext | undefined;

    // ✅ Drag simple
    if (!ctx) {
      onChange({ x: node.x(), y: node.y() });
      store.endGesture?.(); // PR-08
      return;
    }

    const dx = node.x() - ctx.primaryStart.x;
    const dy = node.y() - ctx.primaryStart.y;

    // ✅ update store en 1 batch
    const updates = ctx.ids
      .filter(id => ctx.startPositions[id])
      .map(id => ({
        id,
        updates: {
          x: ctx.startPositions[id].x + dx,
          y: ctx.startPositions[id].y + dy,
        },
      }));

    store.updateShapes(updates);

    node.setAttr('__groupDrag', undefined); // cleanup
    store.endGesture?.(); // 1 seul commitHistory à la fin
  };

  const _handleTransform = useCallback(
    (e: Konva.KonvaEventObject<Event>) => {
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      // P1.6 FIX: Utiliser transformUtils pour centraliser les transformations (live, sans commit)
      applyShapeTransform(shape.id, {
        x: node.x(),
        y: node.y(),
        width: Math.max(5, (node.width() || 100) * scaleX),
        height: Math.max(5, (node.height() || 100) * scaleY),
        rotation: node.rotation(),
      });
    },
    [shape.id]
  );

  const _handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    // P1.6 FIX: Utiliser transformUtils pour centraliser les transformations
    commitShapeTransform(shape.id, {
      x: node.x(),
      y: node.y(),
      width: Math.max(5, node.width() * scaleX),
      height: Math.max(5, node.height() * scaleY),
      rotation: node.rotation(),
    });
  };

  const handleDoubleTap = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (shape.type === 'text' && onDoubleClick) {
        const mouseEvent =
          e.evt instanceof MouseEvent
            ? e.evt
            : new MouseEvent('dblclick', {
                bubbles: true,
                cancelable: true,
                clientX: (e.evt as TouchEvent).touches[0]?.clientX || 0,
                clientY: (e.evt as TouchEvent).touches[0]?.clientY || 0,
              });
        onDoubleClick({ ...e, evt: mouseEvent } as Konva.KonvaEventObject<MouseEvent>);
      }
    },
    [shape.type, onDoubleClick]
  );

  // Cast `as any` à la fin de l'objet : react-konva 19 a durci les types
  // `KonvaNodeEvents` au point que les unions DragEvent/MouseEvent/TouchEvent ne
  // matchent plus quand on construit un objet de props générique. Plutôt que de
  // dupliquer les handlers pour chaque type d'event, on garde l'object littéral
  // et on désactive le check structurel ici uniquement.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const commonProps: any = {
    x: shape.x,
    y: shape.y,
    rotation: shape.rotation || 0,
    opacity: shape.opacity ?? 1,
    visible: shape.visible !== false,
    draggable: !shape.locked, // PR-17: Pas draggable si locked
    listening: !shape.locked, // PR-17: Pas listening si locked
    onClick: handleClick,
    onTap: handleClick,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    // PR-08: Retirer onTransform et onTransformEnd (gérés par le Transformer global)
    id: shape.id,
    onDblClick: shape.type === 'text' ? onDoubleClick : undefined,
    onDblTap: shape.type === 'text' ? handleDoubleTap : undefined,
    onMouseEnter: (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Changer le curseur en "move" au survol
      const container = e.target.getStage()?.container();
      if (container) container.style.cursor = 'move';
      onMouseEnter?.();
    },
    onMouseLeave: (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Remettre le curseur par défaut
      const container = e.target.getStage()?.container();
      if (container) container.style.cursor = 'default';
      onMouseLeave?.();
    },
    stroke: isHovered ? '#3b82f6' : isSelected ? '#3b82f6' : undefined,
    strokeWidth: isHovered ? 2 : isSelected ? 2 : 0,
  };

  switch (shape.type) {
    case 'rect':
      return (
        <Rect
          {...commonProps}
          width={shape.width || 100}
          height={shape.height || 100}
          fill={shape.fill || '#3b82f6'}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          cornerRadius={shape.cornerRadius}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
          globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
        />
      );

    case 'circle':
      return (
        <Circle
          {...commonProps}
          radius={shape.width ? shape.width / 2 : 50}
          fill={shape.fill || '#10b981'}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
          globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
        />
      );

    case 'text': {
      const hasBackground =
        shape.textBackgroundColor &&
        shape.textBackgroundColor !== 'transparent' &&
        shape.textBackgroundColor !== '';
      const padding = shape.textBackgroundPadding || 0;
      const cornerRadius = shape.textBackgroundCornerRadius || 0;
      const textContent = shape.text || 'Texte';
      const fontSize = shape.fontSize || 24;

      const estimatedLines = textContent.split('\n').length || 1;
      const lineHeight = fontSize * 1.2;
      const estimatedHeight = estimatedLines * lineHeight;

      if (hasBackground) {
        // NETTOYÉ: Group parent avec id={shape.id} pour Transformer compatible
        return (
          <Group {...commonProps}>
            <Rect
              x={-padding}
              y={-padding}
              width={(shape.width || textContent.length * fontSize * 0.6) + padding * 2}
              height={estimatedHeight + padding * 2}
              fill={shape.textBackgroundColor}
              cornerRadius={cornerRadius}
              listening={false}
            />
            <Text
              text={textContent}
              fontSize={fontSize}
              fontFamily={shape.fontFamily || 'Arial'}
              fill={shape.fill || '#000000'}
              width={shape.width}
              align={shape.textAlign || 'left'}
              fontStyle={shape.fontStyle}
              textDecoration={shape.textDecoration}
              draggable={false}
              listening={true}
              onClick={handleClick}
              // P3 : KonvaEventObject<TouchEvent> vs MouseEvent — handler unifié.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onTap={handleClick as any}
              onDblClick={onDoubleClick}
              onDblTap={handleDoubleTap}
            />
          </Group>
        );
      }

      return (
        <Text
          {...commonProps}
          text={textContent}
          fontSize={fontSize}
          fontFamily={shape.fontFamily || 'Arial'}
          fill={shape.fill || '#000000'}
          width={shape.width}
          align={shape.textAlign || 'left'}
          fontStyle={shape.fontStyle}
          textDecoration={shape.textDecoration}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
          globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
        />
      );
    }

    case 'triangle': {
      const w = shape.width || 100;
      const h = shape.height || 100;
      const trianglePoints = [w / 2, 0, w, h, 0, h];
      return (
        <Line
          {...commonProps}
          points={trianglePoints}
          closed
          fill={shape.fill || '#f59e0b'}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
          globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
        />
      );
    }

    case 'star':
      return (
        <Star
          {...commonProps}
          numPoints={5}
          innerRadius={(shape.width || 50) / 3}
          outerRadius={(shape.width || 50) / 2}
          fill={shape.fill || '#eab308'}
          stroke={shape.stroke}
          strokeWidth={shape.strokeWidth}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
        />
      );

    case 'heart': {
      const heartSize = shape.width || 80;
      const heartScale = heartSize / 100;
      const heartPath = `M 50 25 
        C 50 15, 35 0, 15 0 
        C 0 0, 0 20, 0 30 
        C 0 55, 50 85, 50 95 
        C 50 85, 100 55, 100 30 
        C 100 20, 100 0, 85 0 
        C 65 0, 50 15, 50 25 Z`;
      return (
        <Path
          {...commonProps}
          data={heartPath}
          fill={shape.fill || '#ef4444'}
          stroke={shape.stroke || '#dc2626'}
          strokeWidth={shape.strokeWidth || 2}
          scaleX={heartScale}
          scaleY={heartScale}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
          globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
        />
      );
    }

    case 'arrow': {
      const arrowLength = shape.width || 100;
      return (
        <Arrow
          {...commonProps}
          points={[0, 0, arrowLength, 0]}
          pointerLength={15}
          pointerWidth={15}
          fill={shape.fill || '#6366f1'}
          stroke={shape.stroke || '#4f46e5'}
          strokeWidth={shape.strokeWidth || 4}
          shadowBlur={shape.shadowBlur || 0}
          shadowColor={shape.shadowColor}
          shadowOffsetX={shape.shadowOffsetX || 0}
          shadowOffsetY={shape.shadowOffsetY || 0}
          globalCompositeOperation={toGlobalCompositeOperation(shape.blendMode)}
        />
      );
    }

    case 'image':
      return <ImageRenderer shape={shape} commonProps={commonProps} />;

    case 'photoSpace':
      // NETTOYÉ: Utiliser id={shape.id} (pas name) pour Transformer compatible
      return (
        <Group
          {...commonProps}
          onDblClick={e => onDoubleClick?.(e)}
          onDblTap={handleDoubleTap}
          className="photoSpace"
          objectType="photoSpace"
          photoIndex={shape.photoIndex || 1}
          photoWidth={shape.width || 200}
          photoHeight={shape.height || 150}
        >
          <Rect
            width={shape.width || 200}
            height={shape.height || 150}
            fill={shape.fill || '#e5e7eb'}
            stroke="#9ca3af"
            strokeWidth={2}
            dash={[10, 5]}
          />
          {shape.src ? (
            <Image
              x={0}
              y={0}
              width={shape.width || 200}
              height={shape.height || 150}
              image={(() => {
                const img = new window.Image();
                img.src = shape.src || '';
                return img;
              })()}
            />
          ) : (
            <Text
              x={0}
              y={(shape.height || 150) / 2 - 10}
              width={shape.width || 200}
              text={`PHOTO ${shape.photoIndex || 1}\nDouble-cliquez pour ajouter`}
              fontSize={14}
              fontFamily="Arial"
              fill="#6b7280"
              align="center"
            />
          )}
        </Group>
      );

    case 'group':
      // NETTOYÉ: Group parent avec id={shape.id} pour Transformer compatible
      return (
        <Group {...commonProps} x={shape.x || 0} y={shape.y || 0}>
          {shape.children?.map(child => (
            <ShapeRenderer
              key={child.id}
              shape={child}
              isSelected={false}
              onSelect={() => {}}
              onChange={() => {}}
              onDoubleClick={() => {}}
            />
          ))}
        </Group>
      );

    default:
      return (
        <Rect
          {...commonProps}
          width={shape.width || 100}
          height={shape.height || 100}
          fill={shape.fill || '#6b7280'}
        />
      );
  }
};
